import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-modal";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { deleteWhatsNew } from "../../../data/ApiEndpoints";
import ModalHeadAndClose from "../../payouts-wtf/common/ModalHeadAndClose";
import WhatsNewForm from "./WhatsNewForm";
import { BodyBuilder } from "../../../exports/Images";
import { BASE_URL } from "../../../data/BaseUrl";

const AllWhatsNew = ({
  callFunction,
  userInfoData,
  modValues,
  setModValues,
  onCloseModalvalues,
  page,
  setPage,
}) => {
  const dispatch = useDispatch();
  const { whatsNewLoading, whatsNewData } = useSelector(
    (state) => state.webApi
  );

  const data = whatsNewData?.data || [];
  const { isModOpen, cardName } = modValues || {};

  const handleDelete = async (uid) => {
    if (uid) {
      const handleConfirm = async () => {
        try {
          const response = await fetch(`${BASE_URL}${deleteWhatsNew}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${userInfoData?.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ uid }),
          });

          const result = await response.json();

          if (result?.status) {
            toast.success(result?.message || "Deleted successfully!");
            callFunction();
          } else {
            toast.error(result?.message || "Cannot delete!");
          }
        } catch (error) {
          toast.error("Failed to delete");
        }
      };

      showConfirmAlert(handleConfirm, `Are you sure, you want to delete!`);
    } else {
      toast.error("UID required!");
    }
  };

  const getExpiryText = (dateAdded) => {
    if (!dateAdded) return "N/A";
    const daysLeft =
      30 -
      Math.floor((Date.now() - parseInt(dateAdded)) / (1000 * 60 * 60 * 24));
    if (daysLeft > 0) return `${daysLeft} days left`;
    return "Expired";
  };

  const getExpiryStyle = (dateAdded) => {
    if (!dateAdded) return {};
    const daysLeft =
      30 -
      Math.floor((Date.now() - parseInt(dateAdded)) / (1000 * 60 * 60 * 24));
    if (daysLeft > 7) {
      return { background: "#E2EFD5", color: "#379C00" };
    } else if (daysLeft > 0) {
      return { background: "#FFF3CD", color: "#856404" };
    }
    return { background: "#F3DADA", color: "#D90000" };
  };

  const tbColumns = [
    {
      dataField: "display_order",
      text: "S.No",
      formatter: (cell, row) => {
        const index = data?.findIndex((item) => item.uid === row.uid);
        return <span className="tb-data">{index !== -1 ? index + 1 : "—"}</span>;
      },
    },
    {
      dataField: "media",
      text: "Media",
      formatter: (cell, row) => {
        // A card carries either a video or an image — show whichever exists.
        if (row?.video) {
          return (
            <video
              src={row.video}
              muted
              playsInline
              preload="metadata"
              style={{
                width: "44px",
                height: "70px",
                objectFit: "cover",
                borderRadius: "8px",
                background: "#000",
                cursor: "pointer",
              }}
              onClick={() =>
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  viewVideo: row.video,
                  cardName: "video",
                }))
              }
            />
          );
        }
        if (row?.image) {
          return (
            <img
              src={row.image}
              alt={row?.name || "NA"}
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
                borderRadius: "8px",
                cursor: "pointer",
              }}
              onClick={() =>
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  viewImage: row.image,
                  cardName: "image",
                }))
              }
            />
          );
        }
        return <span className="tb-data">—</span>;
      },
    },
    {
      dataField: "icon",
      text: "Icon",
      formatter: (cell, row) => {
        return (
          <div style={{ minWidth: "60px" }}>
            {row?.icon ? (
              <img
                src={row.icon}
                alt="icon"
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <span className="tb-data">—</span>
            )}
          </div>
        );
      },
    },
    {
      dataField: "name",
      text: "Name",
      formatter: (cell, row) => {
        return (
          <span className="tb-data minWidth100">{row?.name || "NA"}</span>
        );
      },
    },
    {
      dataField: "heading",
      text: "Heading",
      formatter: (cell, row) => {
        return row?.heading ? (
          <span
            className="tb-data withPadding borderRdius27"
            style={{ background: "#E8F0FE", color: "#1967D2" }}
          >
            {row.heading}
          </span>
        ) : (
          <span className="tb-data">—</span>
        );
      },
    },
    {
      dataField: "description",
      text: "Description",
      formatter: (cell, row) => {
        const desc = row?.description || "";
        return (
          <span
            className="tb-data minWidth150"
            title={desc}
            style={{ maxWidth: "200px" }}
          >
            {desc.length > 50 ? `${desc.substring(0, 50)}...` : desc || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "button",
      text: "Button",
      formatter: (cell, row) => {
        return row?.button_name ? (
          <a
            href={row?.button_link || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="tb-data"
            style={{ color: "#1967D2", textDecoration: "underline" }}
          >
            {row.button_name}
          </a>
        ) : (
          <span className="tb-data">—</span>
        );
      },
    },
    {
      dataField: "display_order",
      text: "Order",
      formatter: (cell, row) => {
        return (
          <span className="tb-data">{row?.display_order ?? "—"}</span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "Created",
      formatter: (cell, row) => {
        if (!row?.date_added) return <span className="tb-data">—</span>;
        const date = new Date(parseInt(row.date_added));
        const formatted = date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
        return <span className="tb-data minWidth100">{formatted}</span>;
      },
    },
    {
      dataField: "expires_in",
      text: "Expires In",
      formatter: (cell, row) => {
        return (
          <span
            className="tb-data withPadding borderRdius27"
            style={getExpiryStyle(row?.date_added)}
          >
            {getExpiryText(row?.date_added)}
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell, row) => {
        return (
          <span
            className="tb-data withPadding borderRdius27"
            style={{
              background: row?.status === "active" ? "#E2EFD5" : "#F3DADA",
              color: row?.status === "active" ? "#379C00" : "#D90000",
            }}
          >
            {row?.status || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (cell, row) => (
        <div className="actionButtonContainer">
          <button
            className="actionBtn icon edit"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                rowData: row,
                cardName: "edit",
              }))
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-pencil-fill"
              viewBox="0 0 16 16"
            >
              <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.5.5 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z" />
            </svg>
          </button>

          <button
            className="actionBtn icon delete"
            onClick={() => handleDelete(row?.uid)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="bi bi-trash3-fill"
              viewBox="0 0 16 16"
            >
              <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        isOpen={isModOpen}
        onRequestClose={onCloseModalvalues}
        contentLabel="WhatsNew Modal"
        ariaHideApp={false}
      >
        <div
          className={`createNewCTOModalContainer`}
          style={{
            width: cardName === "image" ? "auto" : "",
            height: cardName === "image" ? "auto" : "",
          }}
        >
          {cardName !== "add" && cardName !== "edit" ? (
            <ModalHeadAndClose
              title={`${cardName?.replace(/_/g, " ")}`}
              onClose={onCloseModalvalues}
            />
          ) : null}

          {cardName === "add" || cardName === "edit" ? (
            <WhatsNewForm
              userInfoData={userInfoData}
              callFunction={callFunction}
              closeModal={onCloseModalvalues}
              isEdit={cardName === "edit"}
              rowData={modValues.rowData}
            />
          ) : cardName === "image" ? (
            <div className="discussionModalMessage mt30">
              <img
                src={modValues.viewImage}
                alt="preview"
                style={{
                  maxWidth: "500px",
                  maxHeight: "500px",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : cardName === "video" ? (
            <div className="discussionModalMessage mt30">
              <video
                src={modValues.viewVideo}
                controls
                autoPlay
                playsInline
                style={{
                  maxWidth: "500px",
                  maxHeight: "500px",
                  objectFit: "contain",
                  background: "#000",
                  borderRadius: "8px",
                }}
              />
            </div>
          ) : null}
        </div>
      </Modal>

      <div className="fullWidthFlexCenter overflowAuto separatedTable">
        {whatsNewLoading ? (
          <ReactVortoxSpinner />
        ) : (
          <PaymentLinkTable
            tableData={data?.length > 0 ? data : []}
            tableColumns={tbColumns}
            tablePagination={whatsNewData?.pagination || []}
            isPage={page}
            setIsPage={setPage}
          />
        )}
      </div>
    </>
  );
};

export default AllWhatsNew;
